import { store } from '@/store'
import { defineStore } from 'pinia'
import { KeFuConversationApi, KeFuConversationRespVO } from '@/api/mall/promotion/kefu/conversation'
import { KeFuMessageRespVO } from '@/api/mall/promotion/kefu/message'

interface MallKefuInfoVO {
  conversationList: KeFuConversationRespVO[] // 会话列表
  conversationMessageList: Map<number, KeFuMessageRespVO[]> // 会话消息
  /** 列表筛选：undefined=全部；0=平台；>0=店铺 */
  filterShopId: number | undefined
}

export const useMallKefuStore = defineStore('mall-kefu', {
  state: (): MallKefuInfoVO => ({
    conversationList: [],
    conversationMessageList: new Map<number, KeFuMessageRespVO[]>(), // key 会话，value 会话消息列表
    filterShopId: undefined
  }),
  getters: {
    getConversationList(): KeFuConversationRespVO[] {
      return this.conversationList
    },
    getConversationMessageList(): (conversationId: number) => KeFuMessageRespVO[] | undefined {
      return (conversationId: number) => this.conversationMessageList.get(conversationId)
    }
  },
  actions: {
    // ======================= 会话消息相关 =======================
    /** 缓存历史消息 */
    saveMessageList(conversationId: number, messageList: KeFuMessageRespVO[]) {
      this.conversationMessageList.set(conversationId, messageList)
    },

    // ======================= 会话相关 =======================
    /** 加载会话缓存列表 */
    async setConversationList(shopId?: number | null) {
      if (shopId === null) {
        this.filterShopId = undefined
      } else if (shopId !== undefined) {
        this.filterShopId = shopId
      }
      // -1 为「指定店铺未选店」占位，勿请求后端
      if (this.filterShopId !== undefined && this.filterShopId < 0) {
        this.conversationList = []
        return
      }
      const params =
        this.filterShopId === undefined ? undefined : { shopId: this.filterShopId }
      this.conversationList = await KeFuConversationApi.getConversationList(params)
      this.conversationSort()
    },
    /** 更新会话缓存已读 */
    async updateConversationStatus(conversationId: number) {
      if (!this.conversationList?.length) {
        return
      }
      const conversation = this.conversationList.find((item) => item.id === conversationId)
      conversation && (conversation.adminUnreadMessageCount = 0)
    },
    /** 更新会话缓存（列表为空时也要能插入首条；并尊重当前店筛选） */
    async updateConversation(conversationId: number) {
      const conversation = await KeFuConversationApi.getConversation(conversationId)
      if (!conversation) {
        return
      }
      const sid = conversation.shopId != null ? Number(conversation.shopId) : 0
      // filterShopId=-1：指定店铺但未选店，暂不接入任何实时会话
      if (this.filterShopId !== undefined && Number(this.filterShopId) !== sid) {
        return
      }
      this.deleteConversation(conversationId)
      this.conversationList.push(conversation)
      this.conversationSort()
    },
    /** 删除会话缓存 */
    deleteConversation(conversationId: number) {
      const index = this.conversationList.findIndex((item) => item.id === conversationId)
      // 存在则删除
      if (index > -1) {
        this.conversationList.splice(index, 1)
      }
    },
    conversationSort() {
      // 按置顶属性和最后消息时间排序
      this.conversationList.sort((a, b) => {
        // 按照置顶排序，置顶的会在前面
        if (a.adminPinned !== b.adminPinned) {
          return a.adminPinned ? -1 : 1
        }
        // 按照最后消息时间排序，最近的会在前面
        return (b.lastMessageTime as unknown as number) - (a.lastMessageTime as unknown as number)
      })
    }
  }
})

export const useMallKefuStoreWithOut = () => {
  return useMallKefuStore(store)
}
